import { useEffect, useState } from 'react';
import transform from 'lodash/transform';
import find from 'lodash/find';
import { useSelector } from 'react-redux';
import orderBy from 'lodash/orderBy';
import { translate } from 'helpers/translateHelper';
import { useUserCapability, useNewCapability } from 'sharedHooks';
import useGatewayUserCapability from 'sharedHooks/useGatewayUserCapability';
import { assetMenuItems, assetTypes, UNKNOWN_GATEWAY_NAME } from './constants';
import { DeleteAssetComponent } from './ConnectedAssetsDrives/DeleteAsset';
import { DisconnectAssetComponent } from './ConnectedAssetsDrives/DisconnectAsset';
import { ParameterBackupComponent } from './ConnectedAssetsDrives/ParameterBackup';
import { MoveToAnotherGatewayComponent } from './ConnectedAssetsDrives/MoveToAnotherGateway';
import { SwitchToAnotherGatewayComponent } from './ConnectedAssetsDrives/SwitchToAnotherGateway';
import { isEdgeGatewayType, isIOTGatewayType, isCassiaOrArubaGatewayType } from './helpers';
import ExportMessageArchiveComponent from '../Header/ExportMessageArchive/ExportMessageArchiveComponent';

const useHeaderMenus = (selectedAsset, assetType, selectedMenuItemId, gatewayID, gatewaySerialNumber) => {
    const capability = useNewCapability();
    const userCapability = useUserCapability();
    const userGatewayCapability = useGatewayUserCapability();
    const selectedGateway = useSelector(({ powertrainTools }) => powertrainTools?.gateway?.selectedGateway);
    const isIOTGateway = selectedGateway && isIOTGatewayType(selectedGateway);
    const isEdgeGateway = selectedGateway && isEdgeGatewayType(selectedGateway);
    const isCassiaOrAruba = selectedGateway && isCassiaOrArubaGatewayType(selectedGateway);
    const isSmartSenorAsset = assetType === assetTypes.SMART_SENSOR;
    const allHeaderMenus = [
        {
            id: assetMenuItems.DELETE_ASSET,
            availableCapabilityFn: () => isCassiaOrAruba || userCapability.hasAssetAdminCapability(),
            title: translate('ABB.Powertrain.Frontend.DeleteAssetTitle'),
            modalContentComponent: DeleteAssetComponent,
            hidden: !isCassiaOrAruba && (isSmartSenorAsset || !userCapability.hasAssetAdminCapability())
        },
        {
            id: assetMenuItems.DISCONNECT_ASSET,
            availableCapabilityFn: () => gatewaySerialNumber === UNKNOWN_GATEWAY_NAME || userGatewayCapability.hasDriveAdminCapability(),
            title: translate('ABB.Powertrain.Frontend.DisconnectedAssetTitle'),
            modalContentComponent: DisconnectAssetComponent,
            hidden: isSmartSenorAsset || gatewaySerialNumber !== UNKNOWN_GATEWAY_NAME && (isIOTGateway || isEdgeGateway),
            disabled: !gatewayID || gatewaySerialNumber === UNKNOWN_GATEWAY_NAME
        },
        {
            id: assetMenuItems.PARAMETER_BACKUP,
            availableCapabilityFn: () => userCapability.hasTriggerParameterBackupCapability(),
            title: translate('ABB.Powertrain.Frontend.TriggerParameterBackupTitle'),
            modalContentComponent: ParameterBackupComponent,
            hidden: isIOTGateway || isSmartSenorAsset || (gatewaySerialNumber === UNKNOWN_GATEWAY_NAME ? false : !userCapability.hasTriggerParameterBackupCapability()),
        },
        {
            id: assetMenuItems.SWITCH_TO_ANOTHER_GATEWAY,
            availableCapabilityFn: () => gatewaySerialNumber === UNKNOWN_GATEWAY_NAME || userGatewayCapability.hasDriveAdminCapability(),
            title: translate('ABB.Powertrain.Frontend.SwitchToAnotherGateway'),
            modalContentComponent: SwitchToAnotherGatewayComponent,
            hidden: isSmartSenorAsset,
        },
        {
            id: assetMenuItems.MOVE_GATEWAY_ASSET,
            availableCapabilityFn: () => gatewaySerialNumber === UNKNOWN_GATEWAY_NAME || userGatewayCapability.hasDriveAdminCapability(),
            title: translate('ABB.Powertrain.Frontend.MoveToAnotherGateway'),
            modalContentComponent: MoveToAnotherGatewayComponent,
            hidden: isSmartSenorAsset || gatewaySerialNumber !== UNKNOWN_GATEWAY_NAME && (isIOTGateway || isEdgeGateway),
        },
        {
            id: assetMenuItems.EXPORT_MESSAGE_ARCHIVE,
            availableCapabilityFn: () => gatewaySerialNumber !== UNKNOWN_GATEWAY_NAME,
            title: translate('ABB.Powertrain.Frontend.gatewayExportMessageArchiveTitle'),
            modalContentComponent: ExportMessageArchiveComponent,
            hidden: !isSmartSenorAsset,
        }
    ];
    const [headerMenus, setHeaderMenus] = useState([]);
    const [selectedHeaderMenu, setSelectedHeaderMenu] = useState();
    // Always read selectedMenuItem from the store to preserve hook order; prefer prop when provided
    const storeSelectedMenuItem = useSelector(({ detailedInformation }) => detailedInformation.selectedMenuItem);
    const menuItemId = selectedMenuItemId ?? storeSelectedMenuItem;
    useEffect(() => {
        setSelectedHeaderMenu(find(headerMenus, { id: menuItemId }));
    }, [menuItemId, headerMenus]);
    useEffect(() => {
        const menuItemsToRender = transform(allHeaderMenus, (acc, menu) => {
            if (!menu.availableCapabilityFn || menu.availableCapabilityFn(capability) && !menu.hidden) {
                acc.push({ ...menu });
            }
        }, []);
        setHeaderMenus(orderBy(menuItemsToRender, 'title'));
    }, [capability, isCassiaOrAruba, isIOTGateway, isEdgeGateway, isSmartSenorAsset, userCapability, userGatewayCapability, gatewayID, gatewaySerialNumber]);
    return [headerMenus, selectedHeaderMenu];
};
export default useHeaderMenus;
